/**
 * Input Validation and Abuse Prevention Utilities
 * 
 * Provides client-side validation for construction-related queries
 * and helps prevent abuse of the calculation system.
 */

// Construction-related keywords for validation
const CONSTRUCTION_KEYWORDS = [
  // Materials
  'concrete', 'cement', 'drywall', 'sheetrock', 'paint', 'primer',
  'flooring', 'tile', 'hardwood', 'laminate', 'vinyl', 'carpet',
  'lumber', 'wood', '2x4', '2x6', '2x8', '4x4', 'plywood', 'osb',
  'roofing', 'shingle', 'roof', 'felt', 'underlayment',
  'insulation', 'siding', 'brick', 'block', 'stone', 'gravel',
  'sand', 'mortar', 'grout', 'adhesive', 'caulk',
  
  // Project types
  'patio', 'deck', 'fence', 'shed', 'garage', 'basement',
  'bathroom', 'kitchen', 'bedroom', 'room', 'wall', 'ceiling',
  'floor', 'foundation', 'slab', 'walkway', 'driveway',
  'stairs', 'railing', 'trim', 'molding', 'door', 'window',
  
  // Measurements
  'square feet', 'sq ft', 'sqft', 'square foot',
  'cubic yard', 'cubic feet', 'cu ft', 'cuft',
  'linear feet', 'linear foot', 'feet', 'foot', 'ft',
  'inch', 'inches', 'yard', 'meter', 'gallon',
  
  // Actions
  'calculate', 'estimate', 'need', 'require', 'build', 'install',
  'pour', 'frame', 'finish', 'cover', 'coat', 'lay', 'hang',
  
  // Dimensions patterns
  'x', 'by', 'thick', 'deep', 'wide', 'tall', 'high', 'long',
];

// Non-construction patterns to reject
const BLOCKED_PATTERNS = [
  // Coding/tech
  /\b(code|coding|program|javascript|python|html|css|api|function|variable|algorithm)\b/i,
  // General chat
  /\b(weather|recipe|cook|movie|music|game|joke|story|poem)\b/i,
  // Harmful content
  /\b(hack|exploit|illegal|weapon|drug|bomb)\b/i,
  // Other unrelated
  /\b(stock|crypto|bitcoin|invest|dating|relationship)\b/i,
];

// Minimum message length for a valid calculation request
const MIN_MESSAGE_LENGTH = 10;

// Maximum message length to prevent abuse
const MAX_MESSAGE_LENGTH = 1000;
const CONTROL_CHARS_REGEX = /[\u0000-\u001F\u007F]/g; // eslint-disable-line no-control-regex

/**
 * Validates if a message is construction-related
 * @param {string} message - The user's message
 * @returns {{ isValid: boolean, reason?: string, suggestion?: string }}
 */
export const validateConstructionQuery = (message) => {
  if (!message || typeof message !== 'string') {
    return {
      isValid: false,
      reason: 'empty',
      suggestion: 'Please describe your construction project.',
    };
  }

  const trimmedMessage = message.trim();

  // Check minimum length
  if (trimmedMessage.length < MIN_MESSAGE_LENGTH) {
    return {
      isValid: false,
      reason: 'too_short',
      suggestion: 'Please provide more details about your project, including dimensions.',
    };
  }

  // Check maximum length
  if (trimmedMessage.length > MAX_MESSAGE_LENGTH) {
    return {
      isValid: false,
      reason: 'too_long',
      suggestion: 'Please shorten your message. Focus on the key project details.',
    };
  }

  // Check for blocked patterns
  for (const pattern of BLOCKED_PATTERNS) {
    if (pattern.test(trimmedMessage)) {
      return {
        isValid: false,
        reason: 'off_topic',
        suggestion: 'I can only help with construction material calculations. Try describing a building project!',
      };
    }
  }

  // Check for construction keywords
  const lowerMessage = trimmedMessage.toLowerCase();
  const hasConstructionKeyword = CONSTRUCTION_KEYWORDS.some(keyword => 
    lowerMessage.includes(keyword.toLowerCase())
  );

  // Check for dimension patterns (e.g., "10x12", "20 feet", "4 inches")
  const hasDimensionPattern = /\d+\s*(x|by|'|"|ft|feet|foot|inch|inches|sq|sqft|yard|m|cm)\b/i.test(trimmedMessage);

  if (!hasConstructionKeyword && !hasDimensionPattern) {
    return {
      isValid: false,
      reason: 'not_construction',
      suggestion: 'I specialize in construction calculations. Try something like "10x12 concrete patio" or "drywall for 12x14 room".',
    };
  }

  return { isValid: true };
};

/**
 * Checks if a message looks like a follow-up question
 * @param {string} message - The user's message
 * @returns {boolean}
 */
export const isFollowUpQuestion = (message) => {
  if (!message) return false;
  
  const lowerMessage = message.toLowerCase().trim();
  
  // Common follow-up patterns
  const followUpPatterns = [
    /^what (about|if)/i,
    /^how (much|many|about)/i,
    /^can (you|i)/i,
    /^what('s| is) the/i,
    /^also/i,
    /^and /i,
    /^but /i,
    /^instead/i,
    /^(yes|no|ok|okay|sure|thanks)/i,
    /^if (i|we)/i,
    /^same but/i,
    /^change/i,
    /^add /i,
    /^include/i,
    /^what if/i,
  ];

  return followUpPatterns.some(pattern => pattern.test(lowerMessage));
};

/**
 * Extracts dimensions from a message for validation
 * @param {string} message - The user's message
 * @returns {{ hasDimensions: boolean, dimensions: string[] }}
 */
export const extractDimensions = (message) => {
  if (!message) return { hasDimensions: false, dimensions: [] };

  // Match patterns like "10x12", "10 x 12", "10ft x 12ft", "10 feet by 12 feet"
  const dimensionPatterns = [
    /(\d+(?:\.\d+)?)\s*(x|by)\s*(\d+(?:\.\d+)?)/gi,
    /(\d+(?:\.\d+)?)\s*(feet|foot|ft|'|inches|inch|"|yards?|meters?|m)\b/gi,
    /(\d+(?:\.\d+)?)\s*(sq(?:uare)?\.?\s*(?:ft|feet|foot)?)\b/gi,
  ];

  const dimensions = [];
  
  for (const pattern of dimensionPatterns) {
    const matches = message.match(pattern);
    if (matches) {
      dimensions.push(...matches);
    }
  }

  return {
    hasDimensions: dimensions.length > 0,
    dimensions: [...new Set(dimensions)], // Remove duplicates
  };
};

/**
 * Determines the confidence level that this is a valid construction query
 * @param {string} message - The user's message
 * @returns {{ confidence: 'high' | 'medium' | 'low', score: number }}
 */
export const getQueryConfidence = (message) => {
  if (!message) return { confidence: 'low', score: 0 };

  const lowerMessage = message.toLowerCase();
  let score = 0;

  // Check for construction keywords (max 5 points)
  const keywordCount = CONSTRUCTION_KEYWORDS.filter(keyword => 
    lowerMessage.includes(keyword.toLowerCase())
  ).length;
  score += Math.min(keywordCount * 1.5, 5);

  // Check for dimensions (2 points)
  const { hasDimensions } = extractDimensions(message);
  if (hasDimensions) score += 2;

  // Check for question words (1 point)
  if (/\b(how much|how many|calculate|estimate|need)\b/i.test(message)) {
    score += 1;
  }

  // Check for specific material types (2 points)
  if (/\b(concrete|drywall|paint|flooring|lumber|roofing)\b/i.test(message)) {
    score += 2;
  }

  // Determine confidence level
  let confidence;
  if (score >= 6) {
    confidence = 'high';
  } else if (score >= 3) {
    confidence = 'medium';
  } else {
    confidence = 'low';
  }

  return { confidence, score: Math.min(score, 10) };
};

/**
 * Sanitizes user input to prevent injection attacks
 * @param {string} input - The user's input
 * @returns {string}
 */
export const sanitizeInput = (input) => {
  if (!input || typeof input !== 'string') return '';

  return input
    .trim()
    .slice(0, MAX_MESSAGE_LENGTH)
    // Remove potential script tags
    .replace(/<[^>]*>/g, '')
    // Remove excessive whitespace
    .replace(/\s+/g, ' ')
    // Remove control characters
    .replace(CONTROL_CHARS_REGEX, '');
};

/**
 * Rate limiting helper - checks if user can make another request
 * @param {number} lastRequestTime - Timestamp of last request
 * @param {number} cooldownMs - Cooldown period in milliseconds
 * @returns {{ canRequest: boolean, waitTime: number }}
 */
export const checkRateLimit = (lastRequestTime, cooldownMs = 2000) => {
  if (!lastRequestTime) return { canRequest: true, waitTime: 0 };

  const now = Date.now();
  const timeSinceLastRequest = now - lastRequestTime;

  if (timeSinceLastRequest >= cooldownMs) {
    return { canRequest: true, waitTime: 0 };
  }

  return {
    canRequest: false,
    waitTime: cooldownMs - timeSinceLastRequest,
  };
};

const validationUtils = {
  validateConstructionQuery,
  isFollowUpQuestion,
  extractDimensions,
  getQueryConfidence,
  sanitizeInput,
  checkRateLimit,
};

export default validationUtils;
